import { useContext, useEffect, useState } from 'react'
import { FlatList, Pressable, Text, View } from 'react-native'
import { useMutation, useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context'

import { MARK_ALL_NOTIFICATIONS_AS_READ, READ_NOTIFICATION } from '@/constants/graphql/mutations'
import { NOTIFICATION_CONNECTION } from '@/constants/graphql/queries'
import { cn } from '@/utils/cn'
import { useRouter } from '@/utils/navigation'
import { convertNotificationEdge } from '@/utils/notification'
import { useToast } from '@/components/base'
import { ArrowBack, ChevronRight, Notifications, Spinner } from '@/components/icon'
import { Stack } from '@/components/layout'
import LoadingScreen from '@/components/LoadingScreen'
import { NotificationsContext } from '@/components/providers/notifications'
import RefreshControl from '@/components/RefreshControl'
import ScreenHeader from '@/components/ScreenHeader'

type Notification = {
  id: string
  title: string
  content: string
  time: string
  unread: boolean
  action: () => void
}

const PAGE_SIZE = 100

export default function NotificationScreen() {
  const [refreshing, setRefreshing] = useState(false)
  const [isFetchingMore, setIsFetchingMore] = useState(false)
  const { markVisited } = useContext(NotificationsContext)
  const toast = useToast()
  const { t } = useTranslation()
  const router = useRouter()
  const { data, fetchMore, refetch, loading } = useQuery(NOTIFICATION_CONNECTION, {
    variables: { last: PAGE_SIZE },
    onCompleted: () => setRefreshing(false),
    onError: (error) => console.log(error)
  })

  const [markAllRead] = useMutation(MARK_ALL_NOTIFICATIONS_AS_READ, {
    variables: {
      number: 1 // HACK dummy
    },
    onCompleted: () => {
      refetch()
      toast.show({ variant: 'success', description: t('ui:allNotificationsRead') })
    }
  })

  const handleRefetch = async () => {
    setRefreshing(true)
    try {
      await refetch()
    } finally {
      setRefreshing(false)
    }
  }

  useEffect(() => {
    markVisited()
  }, [])

  async function handleFetchMore() {
    if (data?.notificationConnection.pageInfo.hasPreviousPage) {
      setIsFetchingMore(true)
      try {
        await fetchMore({ variables: { last: PAGE_SIZE, before: data?.notificationConnection.pageInfo.startCursor } })
      } finally {
        setIsFetchingMore(false)
      }
    }
  }

  if (loading && !data) {
    return <LoadingScreen />
  }

  const notifications = data
    ? data.notificationConnection.edges.map((edge: any) => convertNotificationEdge(edge.node, t, router)).toReversed()
    : []

  return (
    <>
      <Stack.Screen options={{ header: () => <Header markAllRead={markAllRead} /> }} />
      <SafeAreaView className={'flex-1 bg-background-secondary'}>
        <List
          items={notifications}
          refreshing={refreshing}
          onRefresh={handleRefetch}
          onFetchMore={handleFetchMore}
          isFetchingMore={isFetchingMore}
        />
      </SafeAreaView>
    </>
  )
}

function Header({ markAllRead }: { markAllRead: () => void }) {
  const router = useRouter()
  const { top } = useSafeAreaInsets()
  const { t } = useTranslation()

  return (
    <View className={'border-b border-border-10 bg-background-primary'} style={{ paddingTop: top }}>
      <ScreenHeader
        leftElement={
          <Pressable className={'px-2'} onPress={() => router.back()}>
            {({ pressed }) => <ArrowBack className={cn('size-6 text-text-tertiary', pressed && 'opacity-50')} />}
          </Pressable>
        }
        centerElement={<Text className={'text-base font-medium text-text-primary'}>{t('ui:notifications')}</Text>}
        rightElement={
          <Pressable
            className={'px-2'}
            onPress={() => {
              markAllRead()
            }}
          >
            {({ pressed }) => <Text className={'text-base font-medium text-text-tertiary'}>{t('ui:markAllRead')}</Text>}
          </Pressable>
        }
      />
    </View>
  )
}

interface ListProps {
  items: Notification[]
  refreshing: boolean
  onRefresh: () => void
  onFetchMore: () => void
  isFetchingMore: boolean
}

function List({ items, refreshing, onRefresh, onFetchMore, isFetchingMore }: ListProps) {
  return (
    <FlatList
      data={items}
      onEndReached={onFetchMore}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
      contentContainerStyle={{ flexGrow: 1 }}
      keyExtractor={(item) => item.id}
      ItemSeparatorComponent={() => (
        <View className={'h-px bg-background-primary'}>
          <View className={'ml-6 h-full bg-border-10'} />
        </View>
      )}
      renderItem={({ item, index }) => {
        return (
          <>
            <Item refetch={onRefresh} {...item} />
            {isFetchingMore && index === items.length - 1 && (
              <View className={'mx-auto my-4 animate-spin'}>
                <Spinner className={'size-6 text-primary'} />
              </View>
            )}
          </>
        )
      }}
      ListEmptyComponent={<EmptyList />}
    />
  )
}

type itemProps = Notification & { refetch: () => void }

function Item({ id, title, content, time, unread, action, refetch }: itemProps) {
  const { t } = useTranslation()
  const [markNotifcationAsRead] = useMutation(READ_NOTIFICATION, {
    onCompleted: () => {
      refetch()
    }
  })

  const handleClick = () => {
    if (unread) {
      markNotifcationAsRead({ variables: { number: Number(id) } })
    }
    action()
  }

  const translatedTime = `${t(`day:${time.split(' ')[0]}`)} ${time.split(' ')[1]}`

  return (
    <Pressable onPress={handleClick} className={'bg-background-primary'}>
      <View className={'flex-row p-3 pr-4'}>
        <View className={'flex-1 gap-y-1 pl-3 pr-2'}>
          <View className={'absolute left-0 top-0 h-6 justify-center'}>
            {unread && <View className={'size-1.5 rounded-full bg-danger'} />}
          </View>
          <Text className={'truncate text-base text-text-primary'}>{title}</Text>
          <View className={'gap-y-1.5'}>
            <View>
              <Text
                className={
                  'overflow-hidden text-sm text-text-secondary [-webkit-box-orient:vertical] [-webkit-line-clamp:2] [display:-webkit-box]'
                }
              >
                {content}
              </Text>
            </View>
            <Text className={'text-xs text-text-tertiary'}>{translatedTime}</Text>
          </View>
        </View>
        <View className={'justify-center'}>
          <ChevronRight className={'size-6 text-text-tertiary'} />
        </View>
      </View>
    </Pressable>
  )
}

function EmptyList() {
  const { t } = useTranslation()

  return (
    <View className={'flex-1 items-center justify-center bg-background-secondary'}>
      <View className={'items-center gap-4'}>
        <Notifications className={'size-20 text-text-quaternary'} />
        <Text className={'text-sm text-text-secondary'}>{t('ui:noNotifications')}</Text>
      </View>
    </View>
  )
}
